import React, { useEffect, useState, useRef } from 'react';
import { FileMetadata } from '../types/files';
import { IoDocumentTextOutline } from 'react-icons/io5';
import { ModernFileService } from '../utils/modernFileService';
import { createLogger } from '../utils/logger';

const logger = createLogger({ module: 'ThumbnailPreview' });

// Global thumbnail cache to persist across component instances
const thumbnailCache: Record<string, { url: string; timestamp: number }> = {};
const THUMBNAIL_CACHE_TTL = 10 * 60 * 1000; // 10 minutes

interface Props {
  file: FileMetadata;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const ThumbnailPreview: React.FC<Props> = ({ file, size = 'md', className = '' }) => {
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [previousThumbnailUrl, setPreviousThumbnailUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const fileService = ModernFileService.getInstance();
  const fileIdRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);
  
  // Check if we have a cached thumbnail
  const getCachedThumbnail = (fileId: string): string | null => {
    const cached = thumbnailCache[fileId];
    if (cached && Date.now() - cached.timestamp < THUMBNAIL_CACHE_TTL) {
      return cached.url;
    }
    return null;
  };

  // Set a thumbnail in the cache
  const setCachedThumbnail = (fileId: string, url: string) => {
    thumbnailCache[fileId] = {
      url,
      timestamp: Date.now()
    };
  };

  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (fileIdRef.current !== file.id) {
      // If this is a different file, save the current thumbnail as previous
      if (thumbnailUrl) {
        setPreviousThumbnailUrl(thumbnailUrl);
      }
      
      // Reset states for new file
      setIsLoading(true);
      setHasError(false);
      fileIdRef.current = file.id;
      
      // Check for cached thumbnail first
      const cachedUrl = getCachedThumbnail(file.id);
      if (cachedUrl) {
        logger.debug(`Using cached thumbnail for file ${file.id}`);
        setThumbnailUrl(cachedUrl);
        setIsLoading(false);
        return;
      }
      
      // Set a delay before fetching to prevent too many simultaneous requests
      const delay = Math.random() * 500; // Random delay between 0-500ms
      const delayTimer = setTimeout(() => {
        const loadThumbnail = async () => {
          try {
            // Try to get thumbnail URL
            const url = await fileService.getThumbnailUrl(file);
            
            // Only update if component is still mounted and we're still on the same file
            if (isMountedRef.current && fileIdRef.current === file.id) {
              setThumbnailUrl(url);
              setIsLoading(false);
              // Cache the thumbnail URL if it's not null
              if (url) {
                setCachedThumbnail(file.id, url);
              }
            }
          } catch (error) {
            logger.error('Error loading thumbnail:', error);
            
            // Only update if component is still mounted and we're still on the same file
            if (isMountedRef.current && fileIdRef.current === file.id) {
              setHasError(true);
              setIsLoading(false);
            }
          }
        };

        loadThumbnail();
      }, delay);
      
      // Clean up timeout if component unmounts
      return () => {
        clearTimeout(delayTimer);
      };
    }
  }, [file]);

  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return 'w-8 h-8';
      case 'lg':
        return 'w-16 h-16';
      default:
        return 'w-12 h-12';
    }
  };

  // If we have a previous thumbnail, show it while loading
  if (isLoading && previousThumbnailUrl) {
    return (
      <img
        src={previousThumbnailUrl}
        alt={`Previous thumbnail for ${file.file_name}`}
        className={`${getSizeClasses()} ${className} object-cover rounded opacity-70 transition-opacity duration-300`}
      />
    );
  }

  // If still loading with no previous thumbnail
  if (isLoading) {
    return (
      <div className={`${getSizeClasses()} ${className} bg-gray-100 dark:bg-gray-700 rounded`} />
    );
  }

  // If error or no thumbnail available
  if (hasError || !thumbnailUrl) {
    return (
      <div className={`${getSizeClasses()} ${className} bg-gray-100 dark:bg-gray-700 rounded flex items-center justify-center transition-opacity duration-300`}>
        <IoDocumentTextOutline className="w-1/2 h-1/2 text-gray-400" />
      </div>
    );
  }

  // Successfully loaded thumbnail
  return (
    <img
      src={thumbnailUrl}
      alt={`Thumbnail for ${file.file_name}`}
      className={`${getSizeClasses()} ${className} object-cover rounded transition-opacity duration-300`}
      onError={() => {
        setHasError(true);
        setThumbnailUrl(null);
      }}
    />
  );
};

export default ThumbnailPreview; 